import { useEffect, useMemo, useState } from 'react';
import { Outlet, Navigate, useNavigate } from 'react-router-dom';
import { INTERNAL_ADMIN_ROUTES } from '@cvsi/shared';
import { TopNavigation } from './TopNavigation';
import { useStaff } from '../lib/staffContext';
import { CompanyProvider } from '../lib/companyContext';

/** Global page and workflow search available throughout the authenticated admin portal. */
function CommandSearch() {
  const { session } = useStaff();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const routes = useMemo(() => {
    if (!session) return [];
    return INTERNAL_ADMIN_ROUTES.map((route) => ({
      ...route,
      keywords: `${route.label} ${route.path.replace('/', '').replace(/-/g, ' ')}`.toLowerCase(),
    }));
  }, [session]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return routes.slice(0, 8);
    return routes.filter((route) => route.keywords.includes(q)).slice(0, 8);
  }, [query, routes]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const typing = target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.tagName === 'SELECT';
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
        return;
      }
      if (!typing && e.key === '/') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  function go(path: string) {
    setOpen(false);
    setQuery('');
    navigate(path);
  }

  return (
    <div className="command-search">
      <button className="secondary small command-trigger" onClick={() => setOpen(true)}>Search</button>
      {open && (
        <div className="command-popover" role="dialog" aria-label="Portal search">
          <div className="command-box">
            <input
              autoFocus
              aria-label="Search pages and workflows"
              placeholder="Search pages and workflows"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div className="command-results">
              {matches.length === 0 ? (
                <div className="command-empty">No matching admin page.</div>
              ) : matches.map((route) => (
                <button key={route.path} className="command-result" onClick={() => go(route.path)}>
                  <span>{route.label}</span>
                  <span className="muted">{route.path}</span>
                </button>
              ))}
            </div>
            <div className="command-foot">Page and workflow search. Record search will appear after the backend search index is available.</div>
          </div>
          <button className="command-backdrop" aria-label="Close search" onClick={() => setOpen(false)} />
        </div>
      )}
    </div>
  );
}

/** Authenticated layout with the same top-navigation chrome as the customer portal. */
export function AppShell() {
  const { session } = useStaff();
  if (!session) return <Navigate to="/login" replace />;
  return (
    <CompanyProvider>
      <div className="shell">
        <TopNavigation />
        <main className="main">
          <div className="search-row">
            <CommandSearch />
          </div>
          <Outlet />
        </main>
      </div>
    </CompanyProvider>
  );
}
